<script lang="ts">
  /**
   * MFA-setup-vy för Lyktan (S-04-14).
   *
   * Tre-stegs-flöde för admin-aktivering av TOTP:
   *  1. **intro**: knappen "Aktivera tvåfaktor" → POST /api/admin/mfa/setup.
   *  2. **scan**: backend-svaret innehåller otpauth-URI och secret-base32.
   *     URI:n renderas client-side som SVG-QR (CSP-säkert, inga inline scripts).
   *     Admin scannar med authenticator-app eller skriver in secret manuellt.
   *  3. **verify**: 6-siffrig kod skickas till POST /api/admin/mfa/verify.
   *     Vid 204 anropas `onActivated` som låter App.svelte gömma banner och
   *     navigera tillbaka till föregående vy.
   *
   * Hard-rules:
   *  - secret-base32 + otpauth-URI lagras BARA i `$state`-runes som lever
   *    så länge komponenten är monterad. Vid `cleanup()` (avbryt eller
   *    framgångsrik aktivering) skrivs de över med tom sträng innan vyn
   *    unmountas. Inget i sessionStorage / localStorage.
   *  - Inga `console.log` av otpauthUri eller secretBase32. Båda är
   *    TOTP-secret i klartext och får inte hamna i logghistorik.
   *  - QR-renderingen sker via `qrcode.toDataURL(uri, ...)` som producerar
   *    en base64-PNG i data-URL-form. Vi binder den till `<img src=...>`,
   *    inte `{@html}` — ingen DOM-string-injection, ingen XSS-vektor.
   *    Inga inline scripts, inga eval. CSP-säkert.
   *  - Inga fritextfält. TOTP-input är `inputmode="numeric"` och valideras
   *    klientside (exakt 6 siffror) innan POST.
   *  - Användartexter på svenska. Vi *aktiverar tvåfaktor*, vi släcker inte
   *    autentiseringen.
   *
   * Designspråk (matchar `om.html` + S-04-13):
   *  - Bakgrund `var(--bg-elev)`, border `var(--rule)`, accent `var(--accent)`,
   *    glow `var(--glow)` på aktiv stegindikator och primary-knapp.
   *  - Tre runda glow-pricks som lyser upp sekventiellt — visuellt språk
   *    matchar härd-blomman.
   */
  import { onDestroy } from 'svelte';
  import QRCode from 'qrcode';
  import { requestSetup, verifyAndEnable, verifyResultMessage } from './mfaClient';

  type Props = {
    onActivated: () => void;
    onCancel: () => void;
    onUnauthorized?: () => void;
  };
  const { onActivated, onCancel, onUnauthorized }: Props = $props();

  type Stage = 'intro' | 'scan' | 'verify' | 'success';

  let stage: Stage = $state('intro');
  let isWorking = $state(false);
  let errorMessage: string | null = $state(null);

  // Secret-state. Båda fälten innehåller TOTP-secret i klartext och måste
  // skrivas över när vyn lämnas (cleanup() nedan).
  let otpauthUri: string = $state('');
  let secretBase32: string = $state('');
  // Renderad QR-data-URL (base64 PNG). Fylls async efter requestSetup() lyckas.
  // Vi binder den till `<img src>`, inte `{@html}` — inga DOM-string-injektioner.
  let qrDataUrl: string = $state('');
  // Enkelt UI-feedback för "kopierat"-bekräftelse.
  let copyConfirmation: string | null = $state(null);

  // Verify-stegets input.
  let totpCode: string = $state('');

  // Pending success-timer som triggar onActivated() efter 1.2s. Cleared
  // i onDestroy så att en orphaned closure inte anropas på unmounted vy.
  let successTimerId: number | null = null;

  // Refs för fokus-management vid stage-byten — tangentbordsanvändare
  // ska landa på rätt element direkt utan att behöva tabba dit.
  let scanContinueButton: HTMLButtonElement | undefined = $state();
  let totpInput: HTMLInputElement | undefined = $state();

  $effect(() => {
    if (stage === 'verify') {
      queueMicrotask(() => totpInput?.focus());
    } else if (stage === 'scan') {
      queueMicrotask(() => scanContinueButton?.focus());
    }
  });

  /**
   * Skriver över alla secret-bärande variabler. Anropas vid avbryt,
   * framgångsrik aktivering och `onDestroy`. Effekten är "best effort":
   * Svelte-runes hålls i memory tills GC kör, men vi minimerar livstiden
   * och undviker att de kan synas i debugger-snapshots efter unmount.
   */
  function cleanup(): void {
    otpauthUri = '';
    secretBase32 = '';
    qrDataUrl = '';
    totpCode = '';
    copyConfirmation = null;
  }

  onDestroy(() => {
    cleanup();
    if (successTimerId !== null) {
      window.clearTimeout(successTimerId);
      successTimerId = null;
    }
  });

  async function handleStart(): Promise<void> {
    if (isWorking) return;
    errorMessage = null;
    isWorking = true;
    try {
      const result = await requestSetup();
      if (result.kind === 'unauthorized') {
        errorMessage = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
        return;
      }
      if (result.kind === 'failure') {
        errorMessage = result.message;
        return;
      }
      // OBS: vi loggar ALDRIG result.otpauthUri eller result.secretBase32.
      otpauthUri = result.otpauthUri;
      secretBase32 = result.secretBase32;

      // QR-rendering — pure JS, producerar base64-PNG data-URL. Inga inline
      // scripts, inga eval, inga externa nätverksanrop, ingen DOM-string-
      // injection. CSP-säkert.
      try {
        qrDataUrl = await QRCode.toDataURL(otpauthUri, {
          errorCorrectionLevel: 'M',
          margin: 2,
          // 480 px ger skarp rendering vid 220 px display-storlek på högdpi.
          width: 480,
          color: {
            // Mörk pitch-stil: ljus QR på mörk bakgrund — matchar härd-glöden.
            dark: '#ededed',
            light: '#0a0a0a',
          },
        });
      } catch {
        errorMessage = 'Kunde inte skapa QR-kod. Skriv in secret manuellt nedan.';
        qrDataUrl = '';
      }
      stage = 'scan';
    } finally {
      isWorking = false;
    }
  }

  function goToVerify(): void {
    errorMessage = null;
    totpCode = '';
    stage = 'verify';
  }

  function backToScan(): void {
    errorMessage = null;
    totpCode = '';
    stage = 'scan';
  }

  async function handleVerify(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (isWorking) return;
    errorMessage = null;

    const trimmed = totpCode.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      errorMessage = 'Koden måste vara sex siffror.';
      return;
    }

    isWorking = true;
    try {
      const result = await verifyAndEnable(trimmed);
      if (result.kind === 'ok') {
        // Aktiveringen lyckades. Backend har skrivit `mfa_activate` i
        // admin_actions och secret-bytena finns inte längre i något
        // klient-state efter cleanup().
        cleanup();
        stage = 'success';
        // Liten paus så användaren hinner se bekräftelsen, sedan tillbaka.
        // Spara timer-id så onDestroy kan rensa om vyn unmountas innan
        // 1.2s-fönstret löpt ut (annars anropas onActivated på orphaned closure).
        successTimerId = window.setTimeout(() => {
          successTimerId = null;
          onActivated();
        }, 1200);
        return;
      }
      if (result.kind === 'unauthorized') {
        errorMessage = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
        return;
      }
      if (result.kind === 'not_configured') {
        // Backend förlorade vårt påbörjade secret — förmodligen TTL-utgång.
        // Skicka tillbaka till intro så vi börjar om.
        errorMessage = verifyResultMessage(result);
        cleanup();
        stage = 'intro';
        return;
      }
      errorMessage = verifyResultMessage(result);
      // invalid_code och locked stannar kvar på verify-steget; rate-limit
      // hanteras av backend och vi visar bara texten.
    } finally {
      isWorking = false;
    }
  }

  function handleCancel(): void {
    cleanup();
    onCancel();
  }

  async function copySecret(): Promise<void> {
    if (!secretBase32) return;
    copyConfirmation = null;
    try {
      // Clipboard-API är CSP-säkert — bara siffror+bokstäver, ingen DOM-injection.
      await navigator.clipboard.writeText(secretBase32);
      copyConfirmation = 'Kopierad';
      window.setTimeout(() => {
        copyConfirmation = null;
      }, 2000);
    } catch {
      copyConfirmation = 'Kunde inte kopiera — markera och kopiera manuellt';
    }
  }

  // Stegindikator: tre prickar. Index motsvarar `intro=0`, `scan=1`,
  // `verify=2`. `success` lyser alla tre.
  const STEPS: { stage: Stage; label: string }[] = [
    { stage: 'intro', label: 'Starta' },
    { stage: 'scan', label: 'Scanna' },
    { stage: 'verify', label: 'Verifiera' },
  ];

  function isStepActive(stepStage: Stage): boolean {
    if (stage === 'success') return true;
    const idx = STEPS.findIndex((s) => s.stage === stepStage);
    const cur = STEPS.findIndex((s) => s.stage === stage);
    return idx <= cur;
  }
</script>

<section class="mfa-setup" aria-labelledby="mfa-setup-rubrik">
  <header>
    <p class="eyebrow">Säkerhet</p>
    <h2 id="mfa-setup-rubrik">Aktivera tvåfaktor</h2>
    <p class="lead">
      Tvåfaktor (TOTP) krävs för att hantera användare och inaktivera konton. Använd en
      authenticator-app som Google Authenticator, 1Password eller Aegis.
    </p>
  </header>

  <ol class="steps" aria-label="Stegindikator">
    {#each STEPS as step, i}
      <li
        class="step"
        class:active={isStepActive(step.stage)}
        aria-current={stage === step.stage ? 'step' : undefined}
      >
        <span class="dot" aria-hidden="true"></span>
        <span class="step-label">{i + 1}. {step.label}</span>
      </li>
    {/each}
  </ol>

  {#if errorMessage}
    <div class="error" role="alert">{errorMessage}</div>
  {/if}

  {#if stage === 'intro'}
    <div class="card">
      <p>
        När du klickar på <strong>Aktivera tvåfaktor</strong> genereras ett nytt secret. Det ersätter
        ett ev tidigare påbörjat (men ej aktiverat) secret. Du behöver scanna QR-koden eller skriva in
        secret-strängen i din authenticator-app i nästa steg.
      </p>
      <div class="actions">
        <button type="button" class="secondary" onclick={handleCancel} disabled={isWorking}>
          Avbryt
        </button>
        <button type="button" class="primary" onclick={handleStart} disabled={isWorking}>
          {isWorking ? 'Hämtar…' : 'Aktivera tvåfaktor'}
        </button>
      </div>
    </div>
  {:else if stage === 'scan'}
    <div class="card">
      <h3>Scanna QR-koden</h3>
      <p>
        Öppna din authenticator-app och scanna QR-koden. Om du inte kan scanna kan du skriva in
        secret-strängen manuellt — välj då typen <em>Tidsbaserad (TOTP)</em>, 6 siffror, 30
        sekunders period.
      </p>

      <div class="qr-frame" aria-label="QR-kod för TOTP-secret">
        {#if qrDataUrl}
          <img class="qr-image" src={qrDataUrl} alt="QR-kod för TOTP-secret" />
        {:else}
          <div class="qr-fallback">QR-koden kunde inte visas — använd secret-strängen nedan.</div>
        {/if}
      </div>

      <div class="secret-row">
        <label class="secret-label">
          <span>Secret (manuell inmatning)</span>
          <input
            type="text"
            class="secret-input"
            readonly
            value={secretBase32}
            aria-label="TOTP-secret base32"
          />
        </label>
        <button type="button" class="secondary" onclick={copySecret} disabled={!secretBase32}>
          Kopiera
        </button>
      </div>
      {#if copyConfirmation}
        <p class="copy-feedback" aria-live="polite">{copyConfirmation}</p>
      {/if}

      <div class="actions">
        <button type="button" class="secondary" onclick={handleCancel} disabled={isWorking}>
          Avbryt
        </button>
        <button
          type="button"
          class="primary"
          onclick={goToVerify}
          disabled={isWorking}
          bind:this={scanContinueButton}
        >
          Jag har scannat — fortsätt
        </button>
      </div>
    </div>
  {:else if stage === 'verify'}
    <div class="card">
      <h3>Verifiera koden</h3>
      <p>
        Skriv in den 6-siffriga koden från din authenticator-app. När verifieringen lyckas är
        tvåfaktor aktivt på ditt konto.
      </p>
      <form onsubmit={handleVerify} novalidate>
        <label class="totp-label">
          <span>Kod</span>
          <input
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern={'[0-9]{6}'}
            maxlength="6"
            required
            bind:value={totpCode}
            bind:this={totpInput}
            disabled={isWorking}
            aria-describedby="totp-help"
          />
          <span id="totp-help" class="totp-help">Sex siffror, byts var 30:e sekund.</span>
        </label>
        <div class="actions">
          <button type="button" class="secondary" onclick={backToScan} disabled={isWorking}>
            Tillbaka
          </button>
          <button type="button" class="ghost" onclick={handleCancel} disabled={isWorking}>
            Avbryt
          </button>
          <button type="submit" class="primary" disabled={isWorking || totpCode.length !== 6}>
            {isWorking ? 'Verifierar…' : 'Aktivera'}
          </button>
        </div>
      </form>
    </div>
  {:else}
    <div class="card success-card" role="status">
      <p class="eyebrow success">Klart</p>
      <h3>Tvåfaktor är aktiv</h3>
      <p>
        Du kan nu hantera användare och inaktivera konton. Secret är borttaget från denna sida.
      </p>
    </div>
  {/if}
</section>

<style>
  .mfa-setup {
    max-width: 640px;
    margin: 1rem auto;
    color: var(--text);
  }
  header {
    margin-bottom: 1.5rem;
  }
  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--accent);
    font-size: 0.75rem;
    font-weight: 600;
    margin: 0 0 0.5rem;
  }
  .eyebrow.success {
    color: var(--glow);
  }
  h2 {
    font-size: 1.6rem;
    margin: 0 0 0.5rem;
    letter-spacing: 0.01em;
  }
  h3 {
    font-size: 1.1rem;
    margin: 0 0 0.75rem;
    color: var(--text);
  }
  .lead {
    margin: 0;
    color: var(--text-dim);
    font-size: 0.95rem;
  }
  .steps {
    list-style: none;
    padding: 0;
    margin: 0 0 1.25rem;
    display: flex;
    gap: 1.25rem;
    align-items: center;
  }
  .step {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-dim);
    font-size: 0.85rem;
    letter-spacing: 0.04em;
  }
  .step .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--rule);
    box-shadow: none;
    transition:
      background 200ms ease,
      box-shadow 200ms ease;
  }
  .step.active {
    color: var(--text);
  }
  .step.active .dot {
    background: var(--accent);
    box-shadow:
      0 0 0 2px var(--accent-soft),
      0 0 12px var(--glow);
  }
  .error {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--danger);
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }
  .card {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.5rem;
  }
  .card p {
    color: var(--text-dim);
    line-height: 1.55;
    margin: 0 0 1rem;
  }
  .card p strong {
    color: var(--text);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    flex-wrap: wrap;
    margin-top: 1.25rem;
  }
  button {
    border-radius: 4px;
    padding: 0.6rem 1.1rem;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    letter-spacing: 0.02em;
    transition:
      background 150ms ease,
      border-color 150ms ease,
      color 150ms ease;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  button.primary {
    background: var(--accent);
    color: var(--bg);
    border: 1px solid var(--accent);
  }
  button.primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  button.secondary {
    background: transparent;
    color: var(--accent);
    border: 1px solid var(--accent);
  }
  button.secondary:hover:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  button.ghost {
    background: transparent;
    color: var(--text-dim);
    border: 1px solid var(--rule);
  }
  button.ghost:hover:not(:disabled) {
    color: var(--text);
    border-color: var(--text-dim);
  }
  .qr-frame {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 1rem 0;
    /* QR-koden får svagt glöd-ljus runt sig så den smälter ihop med
       pitch-temat utan att bli oläsbar. */
    box-shadow: inset 0 0 24px rgba(240, 192, 112, 0.05);
  }
  .qr-image {
    width: 220px;
    height: 220px;
    max-width: 100%;
    display: block;
    image-rendering: pixelated;
  }
  .qr-fallback {
    color: var(--text-dim);
    font-size: 0.85rem;
    padding: 2rem;
    text-align: center;
  }
  .secret-row {
    display: flex;
    gap: 0.75rem;
    align-items: end;
    margin-top: 0.75rem;
    flex-wrap: wrap;
  }
  .secret-label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    flex: 1 1 220px;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .secret-input {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.55rem 0.75rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.95rem;
    letter-spacing: 0.08em;
  }
  .secret-input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px var(--accent-soft);
  }
  .copy-feedback {
    color: var(--accent);
    font-size: 0.8rem;
    margin: 0.5rem 0 0;
  }
  .totp-label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.9rem;
    color: var(--text-dim);
  }
  .totp-label input {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.7rem 0.9rem;
    font-size: 1.4rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: 0.4em;
    text-align: center;
    max-width: 12ch;
  }
  .totp-label input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px var(--accent-soft);
  }
  .totp-help {
    font-size: 0.75rem;
    color: var(--text-dim);
  }
  .success-card {
    border-color: var(--accent);
    box-shadow: 0 0 32px rgba(240, 192, 112, 0.15);
  }
</style>
